import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, IconButton, Tooltip,
  TextField, InputAdornment, Avatar, Menu, MenuItem, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Select, FormControl, InputLabel, Alert, useMediaQuery
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { 
  Search, Filter, Download, MoreVertical, Trash2, Flag,
  ArrowUpRight, ArrowDownRight, Receipt, ShoppingBag, DollarSign,
  Plus, FileText, Home, Car
} from 'lucide-react';
import transactionService from '../../services/AGTUps-product-services/transactionService';

// Importa el nuevo wizard para transacciones
import TransactionSetupWizard from './Design-DuesPayments/TransactionSetupWizard';

// Paleta de colores similar a BalanceOverview
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

// Contenedor scrollable para las transacciones
const TransactionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  },
}));

// StyledPaper para secciones en RecentTransactions
const StyledPaperTransactions = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: 'none',
  };
});

// Tarjeta individual de transacción
const TransactionCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    marginBottom: theme.spacing(2),
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(appleColors.dark.ui.accent, 0.15)
        : alpha(appleColors.light.ui.accent, 0.15),
    },
  };
});

// Botón de acción (para filtros, export, etc.)
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  textTransform: 'none',
  gap: theme.spacing(1),
  border: 'none',
  padding: theme.spacing(1, 2),
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'center'
}));

// Botón "Add Transaction"
const AddTransactionButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '30px',
    textTransform: 'none',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0266cc',
      boxShadow: 'none',
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };
});

// StyledSelect para selectores
const StyledSelect = styled(Select)(({ theme }) => {
  const mode = theme.palette.mode;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    borderRadius: '12px',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${mode === 'dark' ? appleColors.dark.system.grey3 : appleColors.light.system.grey3}`,
    '& .MuiSelect-select': {
      padding: '12px 16px',
      fontSize: '0.95rem',
      fontWeight: 500
    },
    '& fieldset': {
      border: 'none'
    }
  };
});

// StyledTextField para el Search
const StyledTextField = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : (theme.palette.mode === 'dark'
          ? appleColors.dark.ui.base
          : appleColors.light.ui.base),
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      '& fieldset': {
        border: 'none',
      },
    },
  };
});

// Categorías estáticas
const categories = [
  { id: 'office', name: 'Office Supplies', icon: ShoppingBag, color: '#4f46e5' },
  { id: 'software', name: 'Software', icon: FileText, color: '#0891b2' },
  { id: 'sales', name: 'Sales', icon: DollarSign, color: '#059669' },
  { id: 'utilities', name: 'Utilities', icon: Home, color: '#7c3aed' },
  { id: 'travel', name: 'Travel', icon: Car, color: '#db2777' }
];

/* ------------------------------------------------------------------------- */
/*                Modal para Eliminar Transacción (DeleteTransactionDialog)  */
/* ------------------------------------------------------------------------- */
const DeleteTransactionDialog = ({ open, onClose, transaction, onDelete }) => {
  const theme = useTheme();

  // Si no hay transacción seleccionada, no renderizamos nada.
  if (!transaction) {
    return null;
  }
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: '1px solid',
          borderColor: theme.palette.mode === 'light' 
            ? 'rgba(0, 0, 0, 0.12)' 
            : 'rgba(255, 255, 255, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Delete Transaction</Typography>
        <IconButton size="small" onClick={onClose}>
          <Trash2 size={18} color={theme.palette.error.main} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
          This action cannot be undone.
        </Alert>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this transaction?
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  {transaction.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {typeof transaction.description === 'object'
                    ? transaction.description.invoice_reference || 'No description available.'
                    : transaction.description || 'No description available.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Amount: ${transaction.amount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Date: {new Date(transaction.date).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: #{transaction.transactionId}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onDelete}
          sx={{
            textTransform: 'none',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }}
        >
          Delete Transaction
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------------- */
/*                        Componente Principal RecentTransactions            */
/* ------------------------------------------------------------------------- */
const RecentTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estados para el listado de transacciones y controles de UI
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  // Usaremos el wizard para agregar transacción en lugar del antiguo diálogo
  const [openAddWizard, setOpenAddWizard] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  // Cargar transacciones desde el backend al montar el componente
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await transactionService.getTransactions();
        // Se asume la respuesta en formato: { success, data: [...] }
        setTransactions(response.data || []);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }
    };
    fetchTransactions();
  }, []);

  const handleMenuOpen = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };  

  const handleDelete = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  // Handler para agregar una nueva transacción usando el wizard
  const handleAddTransaction = async (newTransaction) => {
    try {
      const response = await transactionService.createTransaction(newTransaction);
      if (response.success) {
        setTransactions(prev => [response.data.transaction, ...prev]);
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
    }
  };

  // Handler para eliminar una transacción
  const handleDeleteTransaction = async () => {
    try {
      if (selectedTransaction && selectedTransaction.transactionId) {
        const response = await transactionService.deleteTransaction(selectedTransaction.transactionId);
        if (response.success) {
          setTransactions(prev => prev.filter(tx => tx.transactionId !== selectedTransaction.transactionId));
          setSelectedTransaction(null);
        }
      } else {
        console.warn('No transactionId in selectedTransaction');
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (typeof transaction.description === 'string'
                           ? transaction.description.toLowerCase().includes(searchQuery.toLowerCase())
                           : (transaction.description.invoice_reference
                               ? transaction.description.invoice_reference.toLowerCase().includes(searchQuery.toLowerCase())
                               : false));
    const matchesType = filterType === 'all' || transaction.type === filterType;
    const matchesCategory = filterCategory === 'all' || transaction.category === filterCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  return (
    <TransactionContainer className="space-y-6">
      {/* Control Panel: Search */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledTextField
            fullWidth
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Filter Panel: Selectores */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs="auto">
          <FormControl>
            <InputLabel>Transaction Type</InputLabel>
            <StyledSelect
              value={filterType}
              label="Transaction Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="expense">Expense</MenuItem>
            </StyledSelect>
          </FormControl>
        </Grid>
        <Grid item xs="auto">
          <FormControl>
            <InputLabel>Category</InputLabel>
            <StyledSelect
              value={filterCategory}
              label="Category"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <cat.icon size={16} />
                    {cat.name}
                  </Box>
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>
      </Grid>

      {/* Buttons Panel */}
      <Grid container spacing={2} sx={{ mt: 1, justifyContent: 'flex-end' }}>
        <Grid item>
          <ActionButton
            variant="text"
            onClick={() => {}}
            startIcon={<Filter />}
            sx={{
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
              '&:hover': {
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(3, 133, 255, 0.08)'
              }
            }}
          >
            {!isMobile && 'Filters'}
          </ActionButton>
        </Grid>
        <Grid item>
          <ActionButton
            variant="text"
            startIcon={<Download />}
            sx={{
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
              '&:hover': {
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(3, 133, 255, 0.08)'
              }
            }}
          >
            {!isMobile && 'Export'}
          </ActionButton>
        </Grid>
        <Grid item>
          <AddTransactionButton
            onClick={() => setOpenAddWizard(true)}
            startIcon={<Plus />}
          >
            {!isMobile && 'Add Transaction'}
          </AddTransactionButton>
        </Grid>
      </Grid>

      {/* Transactions List */}
      <StyledPaperTransactions sx={{ mt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
            Recent Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredTransactions.length} transactions
          </Typography>
        </Box>
        {filteredTransactions.map(transaction => (
          <TransactionCard key={transaction.transactionId}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                <Avatar sx={{ bgcolor: transaction.color || '#ccc', width: 40, height: 40 }}>
                  {transaction.icon ? <transaction.icon size={20} /> : <DollarSign size={20} />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {transaction.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {typeof transaction.description === 'object'
                      ? transaction.description.invoice_reference || 'No description available.'
                      : transaction.description || 'No description available.'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(transaction.date).toLocaleDateString()}
                    </Typography>
                    {transaction.receipt && (
                      <Tooltip title="Receipt Available">
                        <Receipt size={14} style={{ color: alpha(theme.palette.text.secondary, 0.7) }} />
                      </Tooltip>
                    )}
                    <Chip 
                      size="small"
                      label={transaction.status}
                      color={transaction.status === 'completed' ? 'success' : 'warning'}
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="subtitle1"
                  color={transaction.type === 'income' ? 'success.main' : 'error.main'}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  {transaction.type === 'income' ? (
                    <ArrowUpRight size={16} style={{ marginRight: 4 }} />
                  ) : (
                    <ArrowDownRight size={16} style={{ marginRight: 4 }} />
                  )}
                  ${transaction.amount.toLocaleString()}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, transaction)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
                    '&:hover': {
                      backgroundColor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <MoreVertical size={16} />
                </IconButton>
              </Box>
            </Box>
          </TransactionCard>
        ))}
      </StyledPaperTransactions>

      {/* Menú de acciones */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {/* Opción para eliminar la transacción */}
        <MenuItem onClick={() => handleDelete(selectedTransaction)} sx={{ color: 'error.main' }}>
          <Trash2 size={16} style={{ marginRight: 8 }} /> Delete Transaction
        </MenuItem>
        {/* Opción para marcar como pendiente */}
        <MenuItem>
          <Flag size={16} style={{ marginRight: 8 }} /> Mark as Pending
        </MenuItem>
      </Menu>

      {/* Modales */}
      {/* Se reemplaza el AddTransactionDialog por el TransactionSetupWizard */}
      <TransactionSetupWizard 
        open={openAddWizard} 
        onClose={() => setOpenAddWizard(false)}
        onSubmit={handleAddTransaction}
        mode="create"
      />
      <DeleteTransactionDialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        transaction={selectedTransaction}
        onDelete={handleDeleteTransaction}
      />
    </TransactionContainer>
  );
};

export default RecentTransactions;
